import { TicketService } from 'src/app/angularJS/services/tickets/ticket.service'; import { SchemeService } from 'src/app/angularJS/services/scheme/scheme.service';


/**
 * A Controller using the {@link actora.services.customerService customerService} to handle the retrieval and display of customer records, also allows for customer search and delete actions to be performed.
 */
export class TicketDetailsController {
  public static $inject = [
    'ticketService',
    '$translate',
    '$location',
    'schemeService'];

  public mainCard: Array<InterACT.Interfaces.IFieldsDisplay>;
  public validityCard: Array<InterACT.Interfaces.IFieldsDisplay>;
  public passengerInfoCard: Array<InterACT.Interfaces.IFieldsDisplay>;
  public multiUseTicketInfoCard: Array<InterACT.Interfaces.IFieldsDisplay>;
  public ticket: InterACT.Interfaces.Tickets.Ticket;

  private currentLocale: string;
  private schemeName: string;

  constructor(
    private ticketService: TicketService,
    private $translate: ng.translate.ITranslateService,
    private $location: ng.ILocationService,
    private schemeService: SchemeService
  ) {
    this.activate();
  }

  /**
   * A method which is used to instantiate the controller
   */
  private activate() {
    this.currentLocale = this.$translate.use();
    const ticketId = this.$location.search()['ticketId'];
    this.ticketService.getTicket(ticketId).then((ticket) => {
      this.ticket = ticket;

      if (this.ticket.scheme && this.ticket.scheme.abbreviation) {
        this.schemeService.getSingleScheme(this.ticket.scheme.link.split('/').pop()).then((response: InterACT.Interfaces.Schemes.SchemeResponse) => {
          this.schemeName = response.scheme.name[this.currentLocale];
          this.configDetails();
        });
      } else {
        this.configDetails();
      }
    });
  }

  /**
   * A method which returns the status of a ticket.
   *
   * @param ticket The ticket to obtain the status for
   */
  private getTicketStatus(ticket: InterACT.Interfaces.Tickets.Ticket) {
    if (ticket.validity && ticket.validity.endDateTime) {
      let expiryDate = new Date(ticket.validity.endDateTime);
      let now = new Date();

      return expiryDate < now ? 'EXPIRED' : ticket.status;
    } else {
      return ticket.status;
    }
  }

  // tslint:disable-next-line: cyclomatic-complexity
  private configDetails = () => {
    this.mainCard = [
      {
        header: 'TICKET_DETAILS.PRODUCT_NAME',
        value: this.ticket.catalogueItem && this.ticket.catalogueItem.name,
        dst: 't-product-name'
      },
      {
        header: 'TICKET_DETAILS.STATUS',
        value: this.getTicketStatus(this.ticket), isBox: true,
        dst: 't-status'
      },
      {
        header: 'TICKET_DETAILS.TECHNOLOGY',
        value: this.ticket.technology,
        dst: 't-technology'
      },
      {
        header: 'TICKET_DETAILS.REFERENCE',
        value: this.ticket.reference,
        dst: 't-reference'
      },
      {
        header: 'TICKET_DETAILS.SCHEME',
        value: this.schemeName
      },
      {
        header: 'TICKET_DETAILS.CREATION_DATE',
        value: this.ticket.creationDateTime,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      },
      {
        header: 'TICKET_DETAILS.LAST_UPDATED',
        value: this.ticket.lastUpdateDateTime,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      },
      {
        header: 'TICKET_DETAILS.FULFILMENT_REQUEST',
        value: this.ticket.fulfilmentRequest && this.ticket.fulfilmentRequest.reference
      },
      {
        header: 'TICKET_DETAILS.ISSUE_DATE',
        value: this.ticket.issueDate,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      },
      {
        header: 'TICKET_DETAILS.PAYMENT_METHOD',
        value: this.ticket.methodOfPayment
      },
      {
        header: 'TICKET_DETAILS.AMOUNT_PAID',
        value: this.ticket.amountPaid && this.ticket.amountPaid.toString(),
        formatType: 'CURRENCY'
      }
    ];

    this.validityCard = [
      {
        header: 'TICKET_DETAILS.VALID_FROM',
        value: (this.ticket.validity && this.ticket.validity.validFrom) && this.ticket.validity.validFrom.name
      },
      {
        header: 'TICKET_DETAILS.VALID_TO',
        value: (this.ticket.validity && this.ticket.validity.validTo) && this.ticket.validity.validTo.name
      },
      {
        header: 'TICKET_DETAILS.START_DATE',
        value: this.ticket.validity && this.ticket.validity.startDateTime,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      },
      {
        header: 'TICKET_DETAILS.END_DATE',
        value: this.ticket.validity && this.ticket.validity.endDateTime,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      }
    ];

    this.passengerInfoCard = [
      {
        header: 'TICKET_DETAILS.NUMBER_OF_ADULTS',
        value: (this.ticket.passengerInfo && this.ticket.passengerInfo.numberOfAdults) && this.ticket.passengerInfo.numberOfAdults.toString()
      },
      {
        header: 'TICKET_DETAILS.NUMBER_OF_CHILDREN',
        value: (this.ticket.passengerInfo && this.ticket.passengerInfo.numberOfChildren) && this.ticket.passengerInfo.numberOfChildren.toString()
      },
      {
        header: 'TICKET_DETAILS.NUMBER_OF_CONCESSIONS',
        value: (this.ticket.passengerInfo && this.ticket.passengerInfo.numberOfConcessions) && this.ticket.passengerInfo.numberOfConcessions.toString()
      }
    ];

    this.multiUseTicketInfoCard = [
      {
        header: 'TICKET_DETAILS.MULTI_USE_INFO.RETURN_TICKET',
        value: this.ticket.multiUseInfo && (this.ticket.multiUseInfo.returnTicket ? 'Yes' : 'No')
      },
      {
        header: 'TICKET_DETAILS.MULTI_USE_INFO.PASS_LENGTH',
        value: (this.ticket.multiUseInfo && this.ticket.multiUseInfo.passLength) && this.ticket.multiUseInfo.passLength.toString()
      },
      {
        header: 'TICKET_DETAILS.MULTI_USE_INFO.CURRENT_PASS_EXPIRY',
        value: this.ticket.multiUseInfo && this.ticket.multiUseInfo.currentPassExpiry,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      },
      {
        header: 'TICKET_DETAILS.MULTI_USE_INFO.ALL_PASSES_EXPIRY',
        value: this.ticket.multiUseInfo && this.ticket.multiUseInfo.allPassesExpiry,
        formatString: 'dd MMM yyyy, HH:mm',
        formatType: 'DATE'
      },
      {
        header: 'TICKET_DETAILS.MULTI_USE_INFO.AUTO_RENEW_ENABLED',
        value: this.ticket.multiUseInfo && (this.ticket.multiUseInfo.autoRenewEnabled ? 'Yes' : 'No')
      },
      {
        header: 'TICKET_DETAILS.MULTI_USE_INFO.REMAINING_PASSES',
        value: (this.ticket.multiUseInfo && this.ticket.multiUseInfo.journeysRemaining) ? this.ticket.multiUseInfo.journeysRemaining.toString() : '0'
      }
    ];
  };
}
